import { useApplicationSettings } from "@/application/providers/application-settings";
import { generate } from "@ant-design/colors";
import {
  ReactNode,
  createContext,
  useContext,
  useMemo,
  CSSProperties,
} from "react";

export type Color =
  | "primary"
  | "accent"
  | "neutral"
  | "success"
  | "warning"
  | "error";

type Palettes = {
  primary: string[];
  accent: string[];
  neutral: string[];
  success: string[];
  warning: string[];
  error: string[];
  [key: string]: string[];
};

export type Palette = Record<Color, string[]>;

const Context = createContext<Palette>({} as Palette);

export const baseColors = {
  primary: "#1481E5",
  accent: "#EE8A0C",
  neutral: "#94A9BD",
  success: "#14b514",
  warning: "#EEB417",
  error: "#D72329",
};

export function PaletteProvider({
  children,
}: {
  children: (value: Palette) => ReactNode;
}) {
  const { accentColor, mainColor } = useApplicationSettings();

  const palette: Palettes = useMemo(() => {
    const colors = {
      ...baseColors,
      primary: mainColor ?? baseColors.primary,
      accent: accentColor ?? baseColors.accent,
    };

    return {
      primary: generate(colors.primary),
      accent: generate(colors.accent),
      neutral: [
        "#edf8fc",
        "#e1ebf0",
        "#d5dee3",
        "#c9d1d6",
        "#bdc4c9",
        "#94a9bd",
        "#6e8196",
        "#4c5c70",
        "#2f3a4a",
        "#161b24",
      ],
      success: [
        "#e2f5df",
        "#b6e8ae",
        "#8adb81",
        "#61cf59",
        "#39c234",
        "#14b514",
        "#098f0d",
        "#016908",
        "#004207",
        "#001c04",
      ],
      warning: [
        "#fffce6",
        "#fff6bd",
        "#ffed94",
        "#ffe16b",
        "#facf41",
        "#eeb417",
        "#c78e0a",
        "#a16b00",
        "#7a4e00",
        "#543200",
      ],
      error: [
        "#fff2f0",
        "#ffd1cc",
        "#fca8a2",
        "#f07773",
        "#e34949",
        "#d72329",
        "#b0131e",
        "#8a0815",
        "#63010e",
        "#3d000a",
      ],
    };
  }, [accentColor, mainColor]);
  const getCssPalette = () => {
    const cssProperties: Record<string, string> = {};

    Object.keys(palette).forEach((key) => {
      palette[key].forEach((color: string, index: number) => {
        const cssVarName = `--palette-${key}-${index}`;
        cssProperties[cssVarName] = color;
      });
    });
    return cssProperties;
  };
  const ccsPalette = getCssPalette();

  return (
    <div
      style={
        {
          ...ccsPalette,
        } as CSSProperties
      }
    >
      <Context.Provider value={palette}>
        <Context.Consumer>{children}</Context.Consumer>
      </Context.Provider>
    </div>
  );
}

export function usePalette() {
  return useContext(Context);
}
